import React, { createContext, useContext, useEffect, useState } from "react"
import {
  calculateTotalBusinessLoss,
  calculateTotalSavings,
  calculateAnnualInvestment,
  calculateBenefits,
  calculateNumberOfTrackedAssets,
} from "../utils/calculations"
import {
  ShipmentsData,
  BusinessExpenseData,
  BusinessImprovementData,
  SmartLabelInvestmentData,
  BenefitsData,
} from "../models/DataModel"
import { useConfig } from "src/hooks/useConfig"

interface CalculatorContextProps {
  shipments: ShipmentsData
  setShipments: (data: ShipmentsData) => void
  expenses: BusinessExpenseData
  setExpenses: (data: BusinessExpenseData) => void
  improvements: BusinessImprovementData
  setImprovements: (data: BusinessImprovementData) => void
  investment: SmartLabelInvestmentData
  setInvestment: (data: SmartLabelInvestmentData) => void
  benefits: BenefitsData | null
  calculateResults: () => void
  reset: () => void
}

const CalculatorContext = createContext<CalculatorContextProps | undefined>(
  undefined
)

export const CalculatorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const config = useConfig()

  const [shipments, setShipments] = useState<ShipmentsData>({
    yearlyVolume: undefined,
    percentageToTrack: 0,
    assetType: "unknown",
  })
  const [expenses, setExpenses] = useState<BusinessExpenseData>({
    expenses: {},
    totalBusinessLoss: 0,
  })
  const [improvements, setImprovements] = useState<BusinessImprovementData>({
    improvements: {},
    improvementAmounts: {},
    totalSavings: 0,
  })
  const [investment, setInvestment] = useState<SmartLabelInvestmentData>({
    labelPrice: 0,
    cloudSubscription: 0,
    labelLifecycle: 0,
    annualInvestment: undefined,
  })
  const [benefits, setBenefits] = useState<BenefitsData | null>(null)

  useEffect(() => {
    if (
      config.labelPrice &&
      config.cloudSubscription &&
      config.labelLifecycle
    ) {
      const labelsCount = Math.max(shipments.yearlyVolume ?? 0, 400)

      const { price } =
        config.labelPrice.find(
          (model) => labelsCount >= model.min && labelsCount <= model.max
        ) ?? config.labelPrice.at(-1)!

      setInvestment((prev) => ({
        ...prev,
        labelPrice: price,
        cloudSubscription: config.cloudSubscription,
        labelLifecycle: config.labelLifecycle,
      }))
    }
  }, [config, shipments])

  const reset = () => {
    setShipments({ yearlyVolume: 0, percentageToTrack: 0, assetType: "" })
    setExpenses({ expenses: {} })
    setImprovements({ improvements: {} })
    setInvestment((prev) => ({ ...prev, annualInvestment: undefined }))
    setBenefits(null)
  }

  const calculateResults = () => {
    const numberOfTrackedAssets = calculateNumberOfTrackedAssets(
      shipments.yearlyVolume ?? 0,
      shipments.percentageToTrack
    )

    setShipments((prev) => ({ ...prev, numberOfTrackedAssets }))

    const totalBusinessLoss = calculateTotalBusinessLoss(expenses.expenses)
    setExpenses((prev) => ({ ...prev, totalBusinessLoss }))

    const improvementAmounts = Object.keys(expenses.expenses).reduce(
      (acc, key) => {
        const expense = expenses.expenses[key] || 0
        const improvementPercentage = improvements.improvements[key] || 0
        acc[key] = expense * (improvementPercentage / 100)
        return acc
      },
      {} as Record<string, number>
    )

    const totalSavings = calculateTotalSavings(
      expenses.expenses,
      improvements.improvements
    )
    setImprovements((prev) => ({
      ...prev,
      improvementAmounts,
      totalSavings,
    }))

    const annualInvestment = calculateAnnualInvestment(
      numberOfTrackedAssets,
      investment
    )
    setInvestment((prev) => ({ ...prev, annualInvestment }))

    const calculatedBenefits = calculateBenefits(totalSavings, annualInvestment)
    setBenefits(calculatedBenefits)
  }

  return (
    <CalculatorContext.Provider
      value={{
        shipments,
        setShipments,
        expenses,
        setExpenses,
        improvements,
        setImprovements,
        investment,
        setInvestment,
        benefits,
        calculateResults,
        reset,
      }}
    >
      {children}
    </CalculatorContext.Provider>
  )
}

export const useCalculator = () => {
  const context = useContext(CalculatorContext)
  if (!context) {
    throw new Error("useCalculator must be used within a CalculatorProvider")
  }
  return context
}
